// src/helpers/proxifyImageUrl.ts
export type ImgOpts = { w?: number; q?: number; dpr?: number; fmt?: string };

const isDev = process.env.NODE_ENV !== 'production';
const IMAGEKIT_URL_ENDPOINT = process.env.IMAGEKIT_URL_ENDPOINT || ''; // optional (only needed for non-ImageKit URLs)

function isImageKitUrl(url: string) {
  return url.includes('ik.imagekit.io') || url.includes('imagekit.io');
}

function buildTr(opts?: ImgOpts) {
  if (!opts) {
    return '';
  }
  const { w, q, dpr, fmt } = opts;

  const parts: string[] = [];
  if (w) {
    parts.push(`w-${Math.round(w)}`);
  }
  if (q) {
    parts.push(`q-${Math.round(q)}`);
  }
  if (dpr) {
    parts.push(`dpr-${dpr}`);
  }
  if (fmt) {
    parts.push(`f-${fmt}`);
  } // "auto" recommended

  return parts.join(',');
}

/**
 * Convert ANY http url into an ImageKit deliverable URL.
 * - ImageKit URL: REPLACE its `tr` param (do not append)
 * - Non-ImageKit URL: route through ImageKit Web Proxy (if endpoint configured)
 */
function toImageKitUrl(src: string, opts?: ImgOpts): string {
  if (!src.startsWith('http')) {
    return src;
  }

  const tr = buildTr(opts);

  // Already on ImageKit => replace tr
  if (isImageKitUrl(src)) {
    if (!tr) {
      return src;
    }
    const u = new URL(src);
    u.searchParams.set('tr', tr); // ✅ REPLACE, don't append
    return u.toString();
  }

  // Non-ImageKit => Web Proxy (needs ImageKit "Web proxy" origin configured)
  if (!IMAGEKIT_URL_ENDPOINT) {
    return src;
  }

  const endpoint = IMAGEKIT_URL_ENDPOINT.replace(/\/+$/, '');
  const trPath = tr ? `/tr:${tr}/` : '/';
  return `${endpoint}${trPath}${src}`;
}

/**
 * Main helper
 */
export function proxifyImageUrl(url: string, opts?: ImgOpts): string {
  if (!url) {
    return url;
  }
  if (isDev) {
    return url;
  }
  if (!url.startsWith('http')) {
    return url;
  }

  const ikUrl = toImageKitUrl(url, opts);

  // ✅ Only pass the final URL to proxy (no extra w/q/fmt params)
  return `/api/image-proxy?url=${encodeURIComponent(ikUrl)}`;
}

/**
 * Generate responsive srcset using widths you decide
 */
export function getProxiedSrcSet(url: string, widths: number[], opts?: Omit<ImgOpts, 'w'>) {
  return widths.map((w) => `${proxifyImageUrl(url, { ...opts, w })} ${w}w`).join(', ');
}

/**
 * Keep this export (you mentioned warnings)
 */
export function proxifyDictionaryImages(obj: any, opts?: ImgOpts): any {
  if (isDev) {
    return obj;
  }

  if (typeof obj === 'string' && obj.startsWith('http')) {
    return proxifyImageUrl(obj, opts);
  }

  if (Array.isArray(obj)) {
    return obj.map((x) => proxifyDictionaryImages(x, opts));
  }

  if (typeof obj === 'object' && obj !== null) {
    const copy: any = {};
    for (const key in obj) {
      copy[key] = proxifyDictionaryImages(obj[key], opts);
    }
    return copy;
  }

  return obj;
}
